import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { ApiResponse, ApiError, CSRFError } from '@/types/api'
import { csrfService } from '@/services/csrfService'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://commutix-server.ddev.site/api'

// Track refresh attempts to prevent infinite loops
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (error?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  failedQueue = []
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Request interceptor to add auth token, CSRF token, and track API activity
apiClient.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add CSRF token for state-changing requests
    if (csrfService.isEnabled() && csrfService.requiresCSRFToken(config.method || 'GET')) {
      try {
        const csrfHeaders = await csrfService.getHeaders()
        Object.assign(config.headers, csrfHeaders)
      } catch (error) {
        console.warn('Failed to get CSRF token for request:', error)
        // Continue with request without CSRF token - let server handle the error
      }
    }

    // Track API activity for session timeout
    if (typeof window !== 'undefined' && (window as any).__sessionTimeout) {
      ;(window as any).__sessionTimeout.trackApiActivity()
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor to handle errors and token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    const apiError: ApiError = {
      message: 'An error occurred',
      status: error.response?.status || 500,
    }

    if (error.response?.data) {
      const errorData = error.response.data as any
      apiError.message = errorData.message || apiError.message
      apiError.errors = errorData.errors
    }

    // Handle network errors
    if (!error.response) {
      apiError.message = 'Network error. Please check your connection.'
      apiError.status = 0
      return Promise.reject(apiError)
    }

    // Handle CSRF errors (419 or specific CSRF error codes)
    if (csrfService.isCSRFError(error) && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Attempt to refresh CSRF token
        const newCSRFToken = await csrfService.handleCSRFError()

        if (newCSRFToken && originalRequest.headers) {
          // Update request with new CSRF token
          originalRequest.headers[csrfService.getConfig().headerName] = newCSRFToken

          // Retry original request
          return apiClient(originalRequest)
        }
      } catch (csrfError) {
        console.error('Failed to handle CSRF error:', csrfError)
        // Continue to other error handling
      }
    }

    // Handle 401 Unauthorized with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token')

      // If no refresh token or this is a refresh request, logout
      if (!refreshToken || originalRequest.url?.includes('/auth/refresh')) {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('token_expires_at')
        localStorage.removeItem('user')

        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }

        return Promise.reject(apiError)
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            // Retry original request with new token
            const token = localStorage.getItem('auth_token')
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return apiClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Attempt token refresh
        const response = await apiClient.post('/auth/refresh', {
          refresh_token: refreshToken,
        })

        const { token, refresh_token, csrf_token } = response.data

        // Update stored tokens
        localStorage.setItem('auth_token', token)
        localStorage.setItem('refresh_token', refresh_token)

        // Update token expiration if provided
        if (response.data.expires_in) {
          const expiresAt = Date.now() + response.data.expires_in * 1000
          localStorage.setItem('token_expires_at', expiresAt.toString())
        }

        // Update CSRF token if provided
        if (csrf_token && csrfService.isEnabled()) {
          csrfService.storeToken(csrf_token, response.data.expires_in)
        }

        // Process queued requests
        processQueue(null, token)

        // Retry original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`
        }

        // Notify session timeout system about token refresh
        if (typeof window !== 'undefined' && (window as any).__sessionTimeout) {
          ;(window as any).__sessionTimeout.trackTokenRefresh()
        }

        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed, process queue with error and logout
        processQueue(refreshError, null)

        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('token_expires_at')
        localStorage.removeItem('user')

        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }

        return Promise.reject(apiError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(apiError)
  },
)

// Generic API methods
export const api = {
  // GET request
  get: async <T>(url: string): Promise<T> => {
    const response = await apiClient.get<ApiResponse<T>>(url)
    return (response.data.data || response.data) as T
  },

  // POST request
  post: async <T>(url: string, data?: any): Promise<T> => {
    const response = await apiClient.post<ApiResponse<T>>(url, data)
    return (response.data.data || response.data) as T
  },

  // PUT request
  put: async <T>(url: string, data?: any): Promise<T> => {
    const response = await apiClient.put<ApiResponse<T>>(url, data)
    return (response.data.data || response.data) as T
  },

  // PATCH request
  patch: async <T>(url: string, data?: any): Promise<T> => {
    const response = await apiClient.patch<ApiResponse<T>>(url, data)
    return (response.data.data || response.data) as T
  },

  // DELETE request
  delete: async <T>(url: string): Promise<T> => {
    const response = await apiClient.delete<ApiResponse<T>>(url)
    return (response.data.data || response.data) as T
  },
}

export default apiClient
